import { useEffect, useState } from "react";
import { X, Users, ScrollText, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Iter387 · Manage Festival Modal — Owner-only.
 * Two tabs: Helper Allocations (add/remove seats, extend passes)
 * and Owner Audit (log of every Owner mutation for this festival).
 */
export default function ManageFestivalModal({ festival, open, onClose, onSaved }) {
  const [tab, setTab] = useState("helpers");
  const [audits, setAudits] = useState([]);
  const [passes, setPasses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [delta, setDelta] = useState({ role: "check_in_helper", n: 1, reason: "" });
  const [extendUntil, setExtendUntil] = useState("");
  const [extendIds, setExtendIds] = useState([]);

  useEffect(() => {
    if (!open || !festival?.id) return;
    (async () => {
      try {
        const [a, p] = await Promise.all([
          api.get(`/festival/${festival.id}/admin-audits`),
          api.get(`/festival/${festival.id}/helper-passes`).catch(() => ({ data: { items: [] } })),
        ]);
        setAudits(a.data.items || []);
        setPasses(p?.data?.items || p?.data?.passes || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load management data");
      }
    })();
  }, [open, festival?.id]);

  if (!open || !festival) return null;

  const applyAllocation = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/festival/${festival.id}/helper-allocation`, {
        role: delta.role,
        delta: Number(delta.n) || 0,
        extend_pass_ids: extendIds.length ? extendIds : undefined,
        extend_until: extendUntil || undefined,
        reason: delta.reason || undefined,
      });
      toast.success("Allocation updated");
      setExtendIds([]);
      setExtendUntil("");
      setDelta({ role: delta.role, n: 1, reason: "" });
      const [a, p] = await Promise.all([
        api.get(`/festival/${festival.id}/admin-audits`),
        api.get(`/festival/${festival.id}/helper-passes`).catch(() => ({ data: { items: [] } })),
      ]);
      setAudits(a.data.items || []);
      setPasses(p?.data?.items || p?.data?.passes || []);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
         onClick={() => !busy && onClose()}
         data-testid="manage-fest-modal">
      <div className="w-full max-w-3xl bg-[#0A0A0A] border-2 border-[#66C2FF] p-5 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="label-mono text-[#66C2FF] text-[10px]">MANAGE FESTIVAL</div>
            <h2 className="h-display text-xl">{festival.name}</h2>
            <div className="text-[10px] uppercase tracking-widest text-[#525252]">
              Organiser: {festival.tenant?.name || festival.tenant?.email || "?"}
            </div>
          </div>
          <button type="button" onClick={() => !busy && onClose()}
                  className="text-[#A3A3A3] hover:text-white"
                  data-testid="manage-fest-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 border-b border-[#262626]">
          <button type="button" onClick={() => setTab("helpers")}
                  data-testid="manage-tab-helpers"
                  className={`px-3 py-2 text-xs uppercase tracking-widest ${tab === "helpers" ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-[#A3A3A3]"}`}>
            <Users className="w-3.5 h-3.5 inline mr-1" /> Helper allocations
          </button>
          <button type="button" onClick={() => setTab("audit")}
                  data-testid="manage-tab-audit"
                  className={`px-3 py-2 text-xs uppercase tracking-widest ${tab === "audit" ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-[#A3A3A3]"}`}>
            <ScrollText className="w-3.5 h-3.5 inline mr-1" /> Owner audit
          </button>
          <div className="flex-1" />
          <Link to={`/festival/${festival.id}`} target="_blank"
                data-testid="manage-open-festival"
                className="text-[10px] uppercase tracking-widest text-[#66C2FF] hover:text-white inline-flex items-center gap-1">
            Open festival <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        {tab === "helpers" && (
          <div className="space-y-4" data-testid="manage-helpers-tab">
            <div className="text-xs text-[#A3A3A3]">
              Current allocation:&nbsp;
              {["check_in_helper", "court_ops_helper", "deputy"].map((r) => (
                <span key={r} className="mr-3 text-white">
                  {r.replace(/_/g, " ")}: <b>{festival.helper_seats_bought?.[r] || 0}</b>
                </span>
              ))}
            </div>
            <div className="grid sm:grid-cols-4 gap-2 items-end">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Role</div>
                <select value={delta.role} onChange={(e) => setDelta({ ...delta, role: e.target.value })}
                        data-testid="alloc-role"
                        className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1">
                  <option value="check_in_helper">Check-in helper</option>
                  <option value="court_ops_helper">Court-ops helper</option>
                  <option value="deputy">Deputy</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Delta (+/−)</div>
                <input type="number" min={-100} max={100} value={delta.n}
                       onChange={(e) => setDelta({ ...delta, n: e.target.value })}
                       data-testid="alloc-delta"
                       className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1" />
              </label>
              <label className="block sm:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Reason (optional)</div>
                <input value={delta.reason} onChange={(e) => setDelta({ ...delta, reason: e.target.value })}
                       data-testid="alloc-reason"
                       placeholder="e.g. good-faith top-up"
                       className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1" />
              </label>
            </div>
            {passes.length > 0 && (
              <div className="border border-[#262626] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-2">
                  Extend existing passes (optional)
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {passes.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs text-white">
                      <input type="checkbox" checked={extendIds.includes(p.id)}
                             onChange={(e) => setExtendIds(e.target.checked
                               ? [...extendIds, p.id]
                               : extendIds.filter((x) => x !== p.id))}
                             data-testid={`alloc-extend-${p.id}`} />
                      <span>{p.holder_name || p.email || p.id.slice(0, 8)} · {p.role} · expires {p.expires_at?.slice(0, 16) || "-"}</span>
                    </label>
                  ))}
                </div>
                <label className="block mt-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Extend until (ISO, blank = festival end)</div>
                  <input value={extendUntil} onChange={(e) => setExtendUntil(e.target.value)}
                         data-testid="alloc-extend-until"
                         placeholder="2026-10-15T18:00:00Z"
                         className="w-full bg-[#141414] border border-[#262626] px-2 py-1 text-white text-xs mt-1" />
                </label>
              </div>
            )}
            <button type="button" disabled={busy} onClick={applyAllocation}
                    data-testid="alloc-apply"
                    className="w-full py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40">
              {busy ? "Applying…" : "Apply allocation change"}
            </button>
          </div>
        )}

        {tab === "audit" && (
          <div className="space-y-2" data-testid="manage-audit-tab">
            {audits.length === 0 && (
              <div className="text-sm text-[#525252] italic">No owner changes recorded yet.</div>
            )}
            {audits.map((a) => (
              <div key={a.id} data-testid={`audit-row-${a.id}`}
                   className="border border-[#262626] bg-[#141414] p-2 text-xs">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <b className="text-[#CCFF00]">{a.action}</b>
                  <span className="text-[10px] text-[#525252]">{a.created_at?.slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="text-[10px] text-[#A3A3A3]">
                  by {a.actor_name || a.actor_email || a.actor_id}
                </div>
                {a.details && Object.keys(a.details).length > 0 && (
                  <pre className="text-[10px] text-[#A3A3A3] mt-1 whitespace-pre-wrap break-all">
                    {JSON.stringify(a.details, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
